import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const airbnbCaseStudy: ContentObject = {
    id: 'airbnb-genai-migration',
    kind: 'system', // From engineering blog
    type: 'case-study',
    name: 'Accelerating Large-Scale Test Migration with LLMs',
    description:
        'This case study illustrates how Airbnb harnessed generative AI to not only accelerate a major code migration but also to significantly enhance developer productivity by automating repetitive, large-scale refactoring tasks while maintaining high code quality and test coverage.',
    url: 'https://medium.com/airbnb-engineering/accelerating-large-scale-test-migration-with-llms-9565c208023b',
    scores: createScores({
        detailedMetrics: 8.0,
        relevanceToUseCase: 9.0,
        reliabilityOfSource: 9.0,
        strategicAndTacticalInformation: 8.0,
        technicalImplementationDetails: 9.0,
        noVendorBias: 10.0,
        relevanceToClient: 8.0,
    }),
    publishedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // Set to 5 days ago as an example
    markdown: `Below is a detailed case study based solely on the provided content:

---

### Who the Source of Information Is

**Source:**
The information comes directly from the Airbnb Tech Blog and is authored by Charles Covey-Brandt. This source provides an insider perspective on how Airbnb leveraged generative AI to drive a large-scale migration of test files.

**Relevant Quote:**
> ""By: Charles Covey-Brandt""
> – Airbnb Tech Blog

---

### Successes

**Overview:**
Airbnb's project achieved a dramatic reduction in the estimated engineering effort. What was initially projected to take 1.5 years was accomplished in just 6 weeks, with automated systems migrating 75% of test files in four hours and ultimately reaching a 97% completion rate through iterative improvements.

**Relevant Quote:**
> ""We'd originally estimated this would take 1.5 years of engineering time to do by hand, but — using a combination of frontier models and robust automation — we finished the entire migration in just 6 weeks.""
> – Charles Covey-Brandt

---

### Challenges

**Overview:**
The migration process involved several challenges:
- **Framework Differences:** Transitioning from Enzyme to React Testing Library (RTL) was not straightforward due to the fundamental differences in how each framework operates.
- **Preservation of Test Intent:** It was critical to ensure that while refactoring, the original test intent and coverage were maintained.
- **Complex Files:** Files with intricate setups or excessive indirection presented unique challenges that required additional context and tailored prompt engineering.

**Relevant Quote:**
> ""However, because of the fundamental differences between these frameworks, we couldn't easily swap out one for the other... we also couldn't just delete the Enzyme files, as analysis showed this would create significant gaps in our code coverage.""
> – Charles Covey-Brandt

---

### Technical Details

**Overview:**
Airbnb built a robust pipeline leveraging large language models (LLMs) to automate the migration process. Key technical aspects included:

- **Step-Based Automation:**
 Files were processed through discrete validation and refactor steps modeled like a state machine. Each file moved to the next state only after passing validation for the previous step.
 > ""We modeled this flow like a state machine, moving the file to the next state only after validation on the previous state passed.""

- **Retry Loops & Dynamic Prompting:**
 To increase success rates, each migration step was retried multiple times. With dynamic prompts that included the latest version of the file and validation errors, the system allowed for continuous improvements until the file passed validation.
 > ""We found the most effective route to improve outcomes was simply brute force: retry steps multiple times until they passed or we reached a limit.""

- **Context-Rich Prompts:**
 For more complex files, the prompts were expanded to include up to 100,000 tokens of context such as component source code, related test files, and manually written examples.
 > ""Each prompt included: The source code of the component under test, The test file we were migrating, Validation failures for the step, Related tests from the same directory, General migration guidelines and common solutions.""

- **Systematic Feedback Loop:**
 The team developed a feedback loop using automated status stamps in files and the ability to re-run specific steps, enabling continuous tuning of the migration process.
 > ""Using these two features, we could quickly run a feedback loop to improve our prompts and tooling.""

---

### Results

**Overview:**
The automated pipeline resulted in significant efficiency gains:
- **Rapid Migration:** An impressive 75% of target files were migrated in four hours during the initial bulk run.
- **High Completion Rate:** After a 4-day tuning process using the "sample, tune, sweep" strategy, the migration success rate climbed to 97%.
- **Cost and Time Savings:** Even with multiple retry loops for the more challenging cases, the automated process delivered a cost-effective solution compared to the manual effort originally estimated.

**Relevant Quote:**
> ""After running this 'sample, tune, sweep' loop for 4 days, we had pushed our completed files from 75% to 97% of the total files, and had just under 100 files remaining.""
> – Charles Covey-Brandt

---

### Vendors Used

**Overview:**
Based on the provided content, no specific external vendors or third-party services were explicitly mentioned in the migration process. The project leveraged "frontier models" and internal tooling built around LLMs, but details on any vendor involvement were not disclosed.

**Relevant Quote:**
> (No specific vendors mentioned in the provided content.)

---

This case study illustrates how Airbnb harnessed generative AI to not only accelerate a major code migration but also to significantly enhance developer productivity by automating repetitive, large-scale refactoring tasks while maintaining high code quality and test coverage.`,
    references: [
        {
            id: '1',
            name: 'Accelerating large-scale test migration with LLMs',
            author: 'Charles Covey-Brandt',
            year: '2024', // Assuming 2024 based on context, adjust if needed
            publisher: 'Airbnb Engineering Blog',
            url: 'https://medium.com/airbnb-engineering/accelerating-large-scale-test-migration-with-llms-9565c208023b',
        },
    ],
};
